import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { LetterAvatar } from "@/components/LetterAvatar";
import { useTheme } from "./ThemeProvider";
import { BRAND_COLOURS } from "@/lib/brandColours";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { LogOut } from "lucide-react";
import { useState, lazy, Suspense } from "react";

/* EVERYTHING this menu opens is lazy (first-load budget). UserAccountMenu is
   imported by AppShell, which wraps every authenticated route, so whatever it
   imports statically is on every visitor's first paint — and none of these is
   on screen until somebody opens the account modal (or a dialog from it).

   The account dialog was the first: it is the last thing in the eager graph
   that imports zod, and zod does not tree-shake — 258 kB (58 kB gzip) on the
   /login critical path for a dialog most sessions never open. The install
   dialog, the two legal dialogs, the appearance pickers and the install panel
   followed on 2026-09-28. Each is mounted only while it is shown, so its chunk
   is fetched then. SidebarFooter already lazy-loads the legal dialogs the same
   way, so the two share one chunk each. */
const AccountSettingsDialog = lazy(() =>
  import("./AccountSettingsDialog").then(m => ({ default: m.AccountSettingsDialog })),
);
const PwaInstallDialog = lazy(() => import("./PwaInstallDialog").then((m) => ({ default: m.PwaInstallDialog })));
const ThemePicker = lazy(() => import("@/components/AppearancePicker").then((m) => ({ default: m.ThemePicker })));
const ColourPicker = lazy(() => import("@/components/AppearancePicker").then((m) => ({ default: m.ColourPicker })));
const InstallAppPanel = lazy(() => import("@/components/InstallAppPanel").then((m) => ({ default: m.InstallAppPanel })));

/** The Appearance tab's shape while its pickers load: three labelled blocks. */
function AppearanceFallback() {
  return (
    <div className="space-y-5" aria-hidden="true">
      <div className="space-y-2">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-11 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
}

// The one brand palette — see lib/brandColours.ts.
const COLORS = BRAND_COLOURS;

interface UserAccountMenuProps {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** The panel only. Its trigger is the menu's foot now (SidebarFooter),
      as in PropPay — the header no longer carries an avatar. */
  hideTrigger?: boolean;
  /** Which tab the modal opens on. */
  initialTab?: "profile" | "security" | "notifications" | "email" | "appearance";
}

export function UserAccountMenu({ isOpen: controlledIsOpen, onOpenChange, hideTrigger, initialTab = "profile" }: UserAccountMenuProps = {}) {
  const { theme, setTheme, effectiveTheme } = useTheme();
  const t = useTranslation();
  const { user, checkSession, logout, updateUser } = useAuth();
  
  // Internal state for uncontrolled mode
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : uncontrolledIsOpen;
  const setIsOpen = (newIsOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newIsOpen);
    } else {
      setUncontrolledIsOpen(newIsOpen);
    }
  };
  
  const [installDialogOpen, setInstallDialogOpen] = useState(false);

  /* Which dialog the menu opened, if any.
  
     A Radix Dialog must be a SIBLING of the DropdownMenu, never a child of it.
     These three used to wrap a DropdownMenuItem as their trigger, which put the
     whole Dialog inside DropdownMenuContent — so the menu's content painted
     above the dialog (it opened BEHIND the menu) and closing the menu unmounted
     its subtree, taking the dialog with it. Both were reported.
  
     `onSelect` without preventDefault lets the menu close on its own, and Radix
     restores focus to the trigger before the dialog mounts, so focus order stays
     correct. The Install App item already worked this way; the other three now
     match it. */
  // Fetch all companies to find user's company custom color
  const { data: companies = [] } = useQuery<any[]>({
    queryKey: ['/api/companies'],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  // Find user's company and get custom color
  // First try to match by user's companyId, fallback to adminIds for legacy
  const userCompanyId = (user as any)?.companyId;
  const userCompany = companies.find((company: any) => 
    company.id === userCompanyId || company.adminIds?.includes(user?.id)
  );
  const companyCustomColor = userCompany?.customColor;

  const updateColorMutation = useMutation({
    mutationFn: async (color: string) => {
      if (!user?.id) return;
      const response = await apiRequest("PUT", `/api/users/${user.id}`, { themeColor: color });
      return await response.json();
    },
    onMutate: async (newColor) => {
      // Instantly update the AuthContext user state for immediate UI update
      updateUser({ themeColor: newColor });
      
      // Return context for potential rollback
      return { previousColor: user?.themeColor };
    },
    onSuccess: (updatedUser) => {
      // Invalidate user queries to ensure cache is synced with server
      // The invalidation will trigger a refetch automatically
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
    },
    onError: (err, newColor, context) => {
      // Rollback to the previous color on error and show error message
      if (context?.previousColor !== undefined) {
        updateUser({ themeColor: context.previousColor });
      }
      console.error('[UserAccountMenu] Failed to update theme color:', err);
    },
  });

  const getName = () => {
    if (!user) return t("shellAcctLoading", "Loading…");
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return name || user.email || t("shellAcctUserFallback", "User");
  };

  const getRole = () => {
    if (!user) return 'No role';
    return user.role === 'SUPER_ADMIN' ? 'SaleFish Admin' : user.userGroupDescription || 'No role';
  };

  const userColor = user?.themeColor || 'salefish';
  
  // Check if user selected custom company color
  const isCustomColor = userColor.startsWith('custom:');
  const customColorValue = isCustomColor ? userColor.substring(7) : null;
  
  const colorData = isCustomColor 
    ? { name: userColor, label: t("companyColour", "Company Colour"), hex: customColorValue!, dark: customColorValue! }
    : (COLORS.find(c => c.name === userColor) || COLORS[0]);
  const isDark = effectiveTheme === 'dark';

  if (!user) {
    return hideTrigger ? null : <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />;
  }

  return (
    <>
      {!hideTrigger && (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        // hit-target: a 36px ring under a 44px thumb — the one tap-small the
        // layout audit still reported. The visual stays 36; the hit area grows.
        className="hit-target rounded-full border-2 border-primary/20 hover:border-primary/40 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        data-testid="button-user-avatar"
        aria-label={t("shellAcctMenuLabel", "User Account Menu")}
      >
        {/* People are their initials on a tint from their name — no photo. */}
        <LetterAvatar name={getName()} size={32} flat />
      </button>
      )}

      {/* ONE account modal (AccountSettingsDialog), tabbed — it used to be this
          panel AND "My Account" opening together. This component still owns
          the appearance state, so it hands those controls in as the
          Appearance tab and Sign out as the footer's leading control. A centred modal on a desk, a bottom sheet on
          a phone, like every panel now. */}
      {isOpen && (
        <Suspense fallback={null}>
          <AccountSettingsDialog
            defaultOpen
            initialTab={initialTab}
            onOpenChange={(next) => { if (!next) setIsOpen(false); }}
            appearance={
        <Suspense fallback={<AppearanceFallback />}>
        <div className="space-y-5">
          <ThemePicker
            value={(theme || 'system') as 'system' | 'light' | 'dark'}
            resolved={effectiveTheme}
            onChange={(v) => setTheme(v as any)}
          />

          <ColourPicker
            choices={[
              ...COLORS.map((c) => ({ value: c.name, label: c.label })),
              ...(companyCustomColor ? [{ value: `custom:${companyCustomColor}`, label: t("companyColour", "Company Colour"), hex: companyCustomColor }] : []),
            ]}
            value={userColor}
            onChange={(v) => updateColorMutation.mutate(v)}
            dark={isDark}
            disabled={updateColorMutation.isPending}
          />

          <InstallAppPanel onInstall={() => setInstallDialogOpen(true)} />
        </div>
        </Suspense>
            }
            footerStart={
              <Button variant="ghost" onClick={() => logout()} data-testid="button-logout" className="text-muted-foreground">
                <LogOut />
                {t("signOut", "Sign Out")}
              </Button>
            }
          />
        </Suspense>
      )}

      {/* Mounted only while open, so its lazy chunk is fetched on demand. It
          renders nothing until it arrives — the tap that opened it is the only
          wait, and it is short. */}
      {installDialogOpen && (
        <Suspense fallback={null}>
          <PwaInstallDialog
            open={installDialogOpen}
            onOpenChange={setInstallDialogOpen}
          />
        </Suspense>
      )}

    </>
  );
}
